import { useCallback, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { elementEvents, ElementType } from 'utils/eventing';
import { MonarchDataConsumer } from 'utils/eventing/monarch/MonarchDataConsumer';
import { StepperOrderDetailsMonarchData } from '../checkout-stepper/components/stepper-order-details/types';
import { CurrencyOption, CurrencySelectionModalProps } from './types';
import './style.scss';

export type { CurrencyOption, CurrencySelectionModalProps } from './types';

export const CurrencySelectionModal = ({
  isOpen,
  isDropdownOpen,
  currencyOptions,
  selectedCurrency,
  onClose,
  onToggleDropdown,
  onCurrencySelect,
}: CurrencySelectionModalProps) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { elementClicked: selectButtonClicked } = elementEvents({
    webElement: {
      elementType: ElementType.Button,
      name: 'currency-modal',
      text: 'Select currency',
      htmlId: 'currency-click',
      location: 'checkout',
    },
  });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle Escape key for modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, handleClose]);

  const handleSelectButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      selectButtonClicked();
      onToggleDropdown();
    },
    [selectButtonClicked, onToggleDropdown],
  );

  const handleCurrencySelect = useCallback(
    (currency: CurrencyOption) => {
      const currencyOptionEvents = elementEvents({
        webElement: {
          elementType: ElementType.Button,
          name: `currency-dropdown`,
          text: `${currency.name}`,
          htmlId: `currency-click`,
          location: 'checkout',
        },
      });

      currencyOptionEvents.elementClicked();
      onCurrencySelect(currency);
    },
    [onCurrencySelect],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="currency-modal-overlay"
      onClick={handleClose}
      role="button"
      tabIndex={0}
      aria-label="Close currency selection modal"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClose();
        }
      }}
    >
      {/* cspell:disable-next-line */}
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="currency-modal-title"
        className="currency-modal-content"
        style={{
          padding: isMobile ? '24px' : '54px',
          height: isMobile ? 'fit-content' : 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Only handle events that should stop propagation
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        {/* cspell:disable-next-line */}
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
        {/* Modal Header */}
        <div
          className="currency-modal-header"
          style={{
            paddingBottom: isMobile ? '8px' : '16px',
            marginBottom: isMobile ? '6px' : '20px',
          }}
        >
          <h1
            id="currency-modal-title"
            className="currency-modal-title"
            style={{ fontSize: isMobile ? '32px' : '56px' }}
          >
            <MonarchDataConsumer<StepperOrderDetailsMonarchData> experimentKey="checkout">
              {(monarchCartData) =>
                monarchCartData?.data?.multiCurrency?.content.headline ? (
                  <span>
                    {monarchCartData.data.multiCurrency.content.headline}
                  </span>
                ) : (
                  <span>Select a currency</span>
                )
              }
            </MonarchDataConsumer>
          </h1>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close currency selection modal"
            className="currency-modal-close-button"
          >
            <div className="currency-modal-close-icon-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M6 6L18 18M6 18L18 6"
                  stroke="#666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>

        {/* Modal Body */}
        <div className="currency-modal-body">
          <p className="currency-modal-description">
            <MonarchDataConsumer<StepperOrderDetailsMonarchData> experimentKey="checkout">
              {(monarchCartData) =>
                monarchCartData?.data?.multiCurrency?.content.description ? (
                  <span>
                    {monarchCartData.data.multiCurrency.content.description}
                  </span>
                ) : (
                  <span style={{ fontSize: isMobile ? '22px' : '24px' }}>
                    Select from the list below to see your price in your
                    currency of choice
                  </span>
                )
              }
            </MonarchDataConsumer>
          </p>

          {/* Currency Select Button */}
          <div className="currency-select-container">
            <button
              type="button"
              onClick={handleSelectButtonClick}
              className={`currency-select-button ${
                isDropdownOpen ? 'currency-select-button--open' : ''
              }`}
            >
              <MonarchDataConsumer<StepperOrderDetailsMonarchData> experimentKey="checkout">
                {(monarchCartData) =>
                  monarchCartData?.data?.multiCurrency?.content.buttonText ? (
                    <span>
                      {monarchCartData.data.multiCurrency.content.buttonText}
                    </span>
                  ) : (
                    <span>Select currency</span>
                  )
                }
              </MonarchDataConsumer>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className={`currency-select-arrow ${
                  isDropdownOpen ? 'currency-select-arrow--open' : ''
                }`}
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke={isDropdownOpen ? 'white' : '#00262B'}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Currency Dropdown List */}
            {isDropdownOpen && (
              <div
                role="listbox"
                aria-label="Currency options"
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    onToggleDropdown();
                  }
                }}
                className="currency-dropdown-list"
              >
                {currencyOptions.map((currency) => (
                  <button
                    key={currency.code}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCurrencySelect(currency);
                    }}
                    className={`currency-dropdown-item ${
                      selectedCurrency?.code === currency.code
                        ? 'currency-dropdown-item--selected'
                        : ''
                    }`}
                  >
                    <span>{currency.flag}</span>
                    <span className="currency-code">
                      {currency.symbol}
                      {currency.code}
                    </span>
                    <span>-</span>
                    <span>{currency.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
